import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useCallback, useEffect, useRef, Suspense } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Database,
  ClipboardList,
  Table2,
  Settings,
  Settings2,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  ArrowLeft,
  Copy,
  RefreshCw,
  MoreHorizontal,
  Loader2,
  FileSpreadsheet,
  AlertTriangle,
  ShieldCheck,
  LayoutGrid,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableConfigPanel, type TableConfigValues } from "@/components/portal/tables/TableConfigPanel";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import { SyncedTopScrollbar } from "@/components/ui/synced-top-scrollbar";
import { cn } from "@/lib/utils";
import {
  usePaginatedData,
  type PaginatedResponse,
  type ServerSortState,
} from "@/hooks/usePaginatedData";
import { myappApi } from "@/services/api/myapp";
import { validateLineNames } from "@/lib/myapp/clone-line-names";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { AppPropertiesSheet } from "@/components/portal/shared/AppPropertiesSheet";
import { useMyAppBatchPermissions } from "@/hooks/useMyAppBatchPermissions";
import { CreateFromWorkbookDialog } from "@/components/portal/tables/CreateFromWorkbookDialog";
import { BulkGroupAccessDialog } from "@/components/portal/groups/BulkGroupAccessDialog";
import { AssignAppDialog } from "@/components/portal/tables/AssignAppDialog";
import { groupsApi } from "@/services/api/groups";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Form, FormCreate, AreaSet, App } from "@/types/myapp";

export const Route = createFileRoute("/portal/myapp/tables")({
  component: TablesPage,
  validateSearch: (search: Record<string, unknown>): { create?: string } => ({
    create: (search.create as string) || undefined,
  }),
});

function TablesPage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading tables...</div>
      </div>
    }>
      <TablesPageContent />
    </Suspense>
  );
}

const TABLE_TYPE_LABELS: Record<string, string> = {
  simple: "Simple",
  advanced: "Advanced",
  latest: "Latest Only",
  versioned: "Versioned",
};

// Edge shadows marking the sticky (pinned) columns so content reads as scrolling
// under them — left for the pinned name group, right for the actions column.
const STICKY_LEFT_SHADOW = "shadow-[4px_0_6px_-4px_rgba(0,0,0,0.12)]";
const STICKY_RIGHT_SHADOW = "shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.12)]";

function TablesPageContent() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can, bypassesGroups, isAdmin, isPlatformAdmin } = usePermissions();

  // Tier 2: RBAC role-based checks
  const canEditTables = can("edit", "tables");
  const canDeleteTables = can("delete", "tables");
  // Creating a table (new, clone, from workbook) is org admin and above only.
  // Deliberately NOT can("create","tables"): CP grants org_member create on the
  // whole MyApp gateway so members can create records, which makes that flag
  // true for members. MyApp enforces the same rule server-side.
  const canCreateTables = can("create", "tables") && (isAdmin || isPlatformAdmin);

  // Page-level permissions (RBAC only — object-level perms gate per-table actions)
  const canCreate = canCreateTables;
  const canImport = canCreateTables;
  const canStructure = canEditTables;
  const canHide = canEditTables;
  const _canDelete = canDeleteTables;
  const canSettings = can("manage", "tables");

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [showHidden, setShowHidden] = useState(false);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [workbookDialogOpen, setWorkbookDialogOpen] = useState(false);
  const [newTable, setNewTable] = useState<FormCreate>({
    name: "",
    description: "",
    table_type: "simple",
  });
  const [areaSets, setAreaSets] = useState<AreaSet[]>([]);
  const [loadingAreaSets, setLoadingAreaSets] = useState(false);

  // Open create dialog from query param (e.g., ?create=simple or ?create=advanced)
  useEffect(() => {
    const createType = search.create;
    if (!canCreate) return;
    if (createType === "simple" || createType === "advanced" || createType === "latest" || createType === "versioned" || createType === "virtual") {
      setNewTable({ name: "", description: "", table_type: createType });
      setCreateDialogOpen(true);
    }
  }, [search.create, canCreate]);

  // Load area sets when area mode is enabled in create dialog
  useEffect(() => {
    if (createDialogOpen && newTable.area_mode && newTable.area_mode !== "none" && areaSets.length === 0) {
      setLoadingAreaSets(true);
      myappApi.areaSets.list(token)
        .then(setAreaSets)
        .catch(() => setAreaSets([]))
        .finally(() => setLoadingAreaSets(false));
    }
  }, [createDialogOpen, newTable.area_mode, token, areaSets.length]);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<Form | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [checkingDeps, setCheckingDeps] = useState(false);
  // Derive the state type from the API function so it always carries every field
  // the endpoint returns (incl. report_impact) and can never drift from myapp.ts.
  const [tableDeps, setTableDeps] = useState<Awaited<
    ReturnType<typeof myappApi.tables.checkDependencies>
  > | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmDeleteText, setConfirmDeleteText] = useState("");

  // Settings sheet state
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Clone dialog state
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [tableToClone, setTableToClone] = useState<Form | null>(null);
  const [cloneName, setCloneName] = useState("");
  const [cloning, setCloning] = useState(false);

  // Post-clone "name your new line tables" dialog. Cloning an advanced
  // (header) table auto-names its line tables "<name> (Copy)" to guarantee a
  // successful clone; this prompts the user to give them intuitive names.
  const [renameLinesOpen, setRenameLinesOpen] = useState(false);
  const [clonedHeaderName, setClonedHeaderName] = useState("");
  const [renameLines, setRenameLines] = useState<
    { id: string; name: string; original: string; error: string | null; validating: boolean }[]
  >([]);
  const [savingLineNames, setSavingLineNames] = useState(false);
  const renameLineTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Group access dialog state

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkGroupAccessOpen, setBulkGroupAccessOpen] = useState(false);

  // App assignment dialog — opened from the bulk bar (selected tables) or a
  // row's "Assign to App…" (that one table). `assignAppItems` is the subject.
  const [assignAppOpen, setAssignAppOpen] = useState(false);
  const [assignAppItems, setAssignAppItems] = useState<
    { id: string; name: string; app_id: string | null }[]
  >([]);

  // Apps for the App column + the assignment dialog. `Form.app_id` is joined
  // client-side to this list; fetched once per organisation alongside the
  // tables and refetched after any assignment.
  const [apps, setApps] = useState<App[]>([]);

  // Group permissions per table (fetched in bulk)
  const [tablePermissions, setTablePermissions] = useState<
    Map<string, { group_name: string; role: string }[]>
  >(new Map());

  const [createNameError, setCreateNameError] = useState<string | null>(null);
  const [createNameValidating, setCreateNameValidating] = useState(false);
  const [cloneNameError, setCloneNameError] = useState<string | null>(null);
  const [cloneNameValidating, setCloneNameValidating] = useState(false);
  const cloneNameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Server-side paginated data fetch — sorting is a server param so it
  // orders the whole dataset, not just the fetched page.
  const fetchTables = useCallback(
    async (
      page: number,
      pageSize: number,
      sort?: ServerSortState,
    ): Promise<PaginatedResponse<Form>> => {
      if (!token || !selectedOrganisation) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      const response = await myappApi.tables.list(token, {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        search: searchTerm || undefined,
        showHidden: showHidden || undefined,
        sortBy: sort?.key as
          | "name"
          | "table_type"
          | "is_published"
          | "created_at"
          | "updated_at"
          | undefined,
        sortDesc: sort ? sort.direction === "desc" : undefined,
      });
      return {
        data: response.items,
        totalCount: response.total,
        page,
        pageSize,
      };
    },
    [token, selectedOrganisation, searchTerm, showHidden],
  );

  const {
    data: tables,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
    requestSort,
    getSortDirection,
  } = usePaginatedData<Form>({
    fetchFn: fetchTables,
    initialPageSize: 25,
    dependencies: [token, selectedOrganisation, searchTerm, showHidden],
    initialSort: { key: "name", direction: "asc" },
  });

  const displayTables = tables;

  // Horizontal scroll sync for the (sticky-header, pinned-column) table: a proxy
  // scrollbar on top mirrors the bottom one so a wide table can be dragged from
  // either end and never bleeds off the page on small screens.
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [tableContentWidth, setTableContentWidth] = useState(0);
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const update = () => setTableContentWidth(el.scrollWidth);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [displayTables.length, isLoading]);

  // Batch permissions — one API call for all visible tables
  const { permissions: tablePerms } = useMyAppBatchPermissions(
    displayTables.map((t) => t.id),
  );

  // Clear selection on data changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm, showHidden, pagination.page]);

  // Selection helpers
  const allOnPageSelected = displayTables.length > 0 && displayTables.every((t) => selectedIds.has(t.id));
  const someOnPageSelected = displayTables.some((t) => selectedIds.has(t.id));

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayTables.map((t) => t.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedItems = displayTables.filter((t) => selectedIds.has(t.id)).map((t) => ({ id: t.id, name: t.name }));

  // Fetch all group permissions for tables (one bulk call)
  const orgId = selectedOrganisation?.organisationId;
  const fetchTablePermissions = useCallback(async () => {
    // cp answers by-object only for org_owner / org_admin / platform_admin (BYPASS_ROLES,
    // `_require_grant_visibility`); anyone else gets a 403 that trips Platform Health (web #1444).
    if (!token || !orgId || !bypassesGroups) return;
    try {
      const resp = await groupsApi.listObjectPermissionsByObject(token, orgId, {
        service_name: "myapp",
        object_type: "table",
      });
      const grouped = new Map<string, { group_name: string; role: string }[]>();
      for (const item of resp.items) {
        const existing = grouped.get(item.object_id) ?? [];
        existing.push({ group_name: item.group_name, role: item.role });
        grouped.set(item.object_id, existing);
      }
      setTablePermissions(grouped);
    } catch {
      // Non-critical — table still works without group badges
    }
  }, [token, orgId, bypassesGroups]);

  useEffect(() => {
    fetchTablePermissions();
  }, [fetchTablePermissions, tables]);

  const fetchApps = useCallback(async () => {
    if (!token || !orgId) return;
    try {
      // The data-plane caps a page at 100; walk every page so an org with
      // more Apps than that never shows "—" for a table that IS assigned.
      const PAGE = 100;
      const all: App[] = [];
      for (let offset = 0; offset < PAGE * 20; offset += PAGE) {
        const resp = await myappApi.apps.list(token, { includeInactive: true, limit: PAGE, offset });
        // A malformed page (no `items`) must not take the whole list down —
        // the App column is decoration on the Tables page, not its subject.
        const items = Array.isArray(resp?.items) ? resp.items : [];
        all.push(...items);
        if (items.length < PAGE || all.length >= (resp?.total ?? 0)) break;
      }
      setApps(all);
    } catch {
      // Non-critical — the App column shows "—" until the list loads
    }
  }, [token, orgId]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const appsById = new Map(apps.map((a) => [a.id, a]));

  const openAssignApp = (items: Form[]) => {
    setAssignAppItems(items.map((t) => ({ id: t.id, name: t.name, app_id: t.app_id ?? null })));
    setAssignAppOpen(true);
  };

  // Handlers
  const handleToggleHidden = async (table: Form) => {
    if (!token) return;
    try {
      await myappApi.tables.update(token, table.id, { is_hidden: !table.is_hidden });
      refresh();
    } catch (err) {
      console.error("Failed to update table:", err);
    }
  };

  const handleToggleLock = async (table: Form) => {
    if (!token) return;
    try {
      await myappApi.tables.update(token, table.id, { is_locked: !table.is_locked });
      refresh();
    } catch (err) {
      console.error("Failed to update table:", err);
    }
  };

  const handleCreateClick = () => {
    setNewTable({ name: "", description: "", table_type: "simple" });
    setCreateNameError(null);
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!newTable.name.trim() || !token) return;

    try {
      setCreating(true);
      const form = await myappApi.tables.create(token, newTable);
      setCreateDialogOpen(false);
      setNewTable({ name: "", description: "" });
      navigate({ to: "/portal/myapp/$tableId/edit", params: { tableId: String(form.id) } });
    } catch (err) {
      console.error("Failed to create table:", err);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = async (table: Form) => {
    setTableToDelete(table);
    setTableDeps(null);
    setDeleteError(null);
    setConfirmDeleteText("");
    setCheckingDeps(true);
    setDeleteDialogOpen(true);

    try {
      const deps = await myappApi.tables.checkDependencies(token, table.id);
      setTableDeps(deps);
    } catch (err) {
      console.error("Failed to check dependencies:", err);
      setDeleteError("Failed to check table dependencies. Please try again.");
    } finally {
      setCheckingDeps(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!tableToDelete || !token) return;

    try {
      setDeleting(true);
      setDeleteError(null);
      await myappApi.tables.deleteWithConfirm(
        token,
        tableToDelete.id,
        confirmDeleteText,
        tableToDelete.is_published,
      );
      setDeleteDialogOpen(false);
      setTableToDelete(null);
      setTableDeps(null);
      refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete table";
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  };

  const handleCloneClick = (table: Form) => {
    setTableToClone(table);
    const defaultName = `${table.name} (Copy)`;
    setCloneName(defaultName);
    setCloneNameError(null);
    setCloneNameValidating(true);
    setCloneDialogOpen(true);
    // Validate the default clone name
    myappApi.tables.validateName(token, defaultName).then((result) => {
      if (!result.is_valid) setCloneNameError(result.message);
      setCloneNameValidating(false);
    }).catch(() => setCloneNameValidating(false));
  };

  const handleCloneConfirm = async () => {
    if (!tableToClone || !cloneName.trim() || !token) return;

    try {
      setCloning(true);
      const newHeader = await myappApi.tables.clone(
        token,
        tableToClone.id,
        cloneName,
      );
      const wasHeader = tableToClone.is_header_table;
      const newHeaderName = newHeader.name;
      setCloneDialogOpen(false);
      setTableToClone(null);
      setCloneName("");
      refresh();

      // For an advanced table, surface the auto-named line tables so the
      // user can rename them to something intuitive (they were cloned as
      // "<name> (Copy)" to guarantee uniqueness).
      if (wasHeader) {
        try {
          const children = await myappApi.tables.getChildren(token, newHeader.id);
          if (children.length > 0) {
            setClonedHeaderName(newHeaderName);
            setRenameLines(
              children.map((c) => ({
                id: c.id,
                name: c.name,
                original: c.name,
                error: null,
                validating: false,
              })),
            );
            setRenameLinesOpen(true);
          }
        } catch (err) {
          console.error("Failed to load cloned line tables:", err);
        }
      }
    } catch (err) {
      console.error("Failed to clone table:", err);
    } finally {
      setCloning(false);
    }
  };

  const handleRenameLineChange = (id: string, value: string) => {
    setRenameLines((prev) =>
      prev.map((l) =>
        l.id === id
          ? { ...l, name: value, error: null, validating: !!value.trim() }
          : l,
      ),
    );
    const timers = renameLineTimers.current;
    const existing = timers.get(id);
    if (existing) clearTimeout(existing);
    if (!value.trim()) {
      setRenameLines((prev) =>
        prev.map((l) =>
          l.id === id
            ? { ...l, validating: false, error: "Name is required" }
            : l,
        ),
      );
      return;
    }
    timers.set(
      id,
      setTimeout(async () => {
        try {
          const result = await myappApi.tables.validateName(
            token,
            value.trim(),
            id,
          );
          setRenameLines((prev) =>
            prev.map((l) =>
              l.id === id
                ? {
                    ...l,
                    error: result.is_valid ? null : result.message,
                    validating: false,
                  }
                : l,
            ),
          );
        } catch {
          // Backend enforces uniqueness on save.
          setRenameLines((prev) =>
            prev.map((l) =>
              l.id === id ? { ...l, validating: false } : l,
            ),
          );
        }
      }, 400),
    );
  };

  const clearRenameLineTimers = () => {
    renameLineTimers.current.forEach((t) => clearTimeout(t));
    renameLineTimers.current.clear();
  };

  const handleSaveLineNames = async () => {
    if (!token) return;

    // Guard against blank names and duplicates within the dialog (the backend
    // would reject these per-row, but catching them up front avoids a partial,
    // non-atomic rename). Computed synchronously — not inside a state updater.
    const { ok, errors } = validateLineNames(renameLines);
    if (!ok) {
      setRenameLines((prev) =>
        prev.map((l) => (errors[l.id] ? { ...l, error: errors[l.id] } : l)),
      );
      return;
    }
    // Also block if an async name check is still pending or already errored.
    if (renameLines.some((l) => l.error || l.validating)) return;

    try {
      setSavingLineNames(true);
      const changed = renameLines.filter((l) => l.name.trim() !== l.original);
      for (const line of changed) {
        try {
          await myappApi.tables.update(token, line.id, { name: line.name.trim() });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Failed to rename line table";
          setRenameLines((prev) =>
            prev.map((l) => (l.id === line.id ? { ...l, error: message } : l)),
          );
          return;
        }
      }
      clearRenameLineTimers();
      setRenameLinesOpen(false);
      setRenameLines([]);
      refresh();
    } finally {
      setSavingLineNames(false);
    }
  };

  const handleSkipLineNames = () => {
    clearRenameLineTimers();
    setRenameLinesOpen(false);
    setRenameLines([]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          onSettingsClick={() => setSettingsOpen(true)}
          onCreateClick={handleCreateClick}
          onCreateFromWorkbookClick={() => setWorkbookDialogOpen(true)}
          canCreate={canCreate}
          canImport={canImport}
          canSettings={canSettings}
        />
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <p className="text-destructive">{error}</p>
            <Button onClick={refresh} variant="outline">Try Again</Button>
          </div>
        </div>
        <SettingsSheet
          selectedOrganisation={selectedOrganisation}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        onSettingsClick={() => setSettingsOpen(true)}
        onCreateClick={handleCreateClick}
        onCreateFromWorkbookClick={() => setWorkbookDialogOpen(true)}
        canCreate={canCreate}
        canImport={canImport}
        canSettings={canSettings}
      />

      {/* Content */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search tables..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <div className="flex items-center space-x-2">
            <Switch
              id="show-hidden"
              checked={showHidden}
              onCheckedChange={setShowHidden}
            />
            <Label htmlFor="show-hidden">Show hidden</Label>
          </div>
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <span className="text-sm font-medium">
              {selectedIds.size} table{selectedIds.size !== 1 ? "s" : ""} selected
            </span>
            <Button
              size="sm"
              onClick={() => setBulkGroupAccessOpen(true)}
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Group Access
            </Button>
            {canSettings && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => openAssignApp(displayTables.filter((t) => selectedIds.has(t.id)))}
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                Add to App
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : displayTables.length === 0 ? (
          <div className="text-center py-12">
            <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No tables match your search" : "No tables yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try a different search term."
                : "Create your first table to start collecting data"}
            </p>
            {!searchTerm && canCreate && (
              <Button onClick={handleCreateClick}>
                <Plus className="h-4 w-4 mr-2" />
                Create Table
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Proxy top scrollbar — mirrors the table's bottom scroll so a wide
                table can be dragged from the top too and never bleeds off small
                screens. Hidden when the table fits. */}
            <SyncedTopScrollbar
              targetRef={tableScrollRef}
              contentWidth={tableContentWidth}
              className="bg-muted/40 rounded-t-md"
            />
            {/* Bounded scroll container so the header, first column and the
                right-hand actions can stay pinned while the rest scrolls. */}
            <div ref={tableScrollRef} className="overflow-auto max-h-[70vh]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px] px-3 sticky top-0 left-0 z-30 bg-background">
                    <Checkbox
                      checked={allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <SortableTableHead
                    direction={getSortDirection("name")}
                    onSort={() => requestSort("name")}
                    className={cn(
                      "sticky top-0 left-10 z-30 bg-background",
                      STICKY_LEFT_SHADOW,
                    )}
                  >
                    Name
                  </SortableTableHead>
                  {/* Not sortable: GET /tables has no `app` sort key, and a
                      client-side reorder of one server page is exactly what
                      tables-server-sort.spec.ts forbids. */}
                  <TableHead className="sticky top-0 z-20 bg-background">
                    App
                  </TableHead>
                  <SortableTableHead
                    direction={getSortDirection("table_type")}
                    onSort={() => requestSort("table_type")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Type
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("is_published")}
                    onSort={() => requestSort("is_published")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("created_at")}
                    onSort={() => requestSort("created_at")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Created
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("updated_at")}
                    onSort={() => requestSort("updated_at")}
                    className="sticky top-0 z-20 bg-background"
                  >
                    Updated
                  </SortableTableHead>
                  <TableHead className="sticky top-0 z-20 bg-background">
                    Access groups
                  </TableHead>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                    className={cn(
                      "sticky top-0 right-0 z-30 bg-background text-right",
                      STICKY_RIGHT_SHADOW,
                    )}
                  >
                    Actions
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayTables.map((table) => (
                  <TableRow key={table.id}>
                    <TableCell className="w-[40px] px-3 sticky left-0 z-10 bg-background">
                      <Checkbox
                        checked={selectedIds.has(table.id)}
                        onCheckedChange={() => toggleSelect(table.id)}
                      />
                    </TableCell>
                    <TableCell
                      className={cn(
                        "sticky left-10 z-10 bg-background",
                        STICKY_LEFT_SHADOW,
                      )}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <Link
                            to="/portal/myapp/$tableId"
                            params={{ tableId: String(table.id) }}
                            className={`font-medium hover:underline text-primary ${table.is_deleted ? "line-through opacity-60" : ""}`}
                          >
                            {table.name}
                          </Link>
                          {table.is_hidden && (
                            <Badge variant="outline" className="text-muted-foreground">
                              Hidden
                            </Badge>
                          )}
                          {table.is_deleted && (
                            <Badge variant="destructive" className="text-xs">
                              Deleted{table.deleted_by_user_email ? ` by ${table.deleted_by_user_email}` : ""}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            {table.table_name}
                          </code>
                          {table.description && (
                            <span className="text-xs text-muted-foreground truncate max-w-[300px]">
                              {table.description}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-testid="table-app-cell">
                      {(() => {
                        const app = table.app_id ? appsById.get(table.app_id) : undefined;
                        if (!app) return <span className="text-muted-foreground">—</span>;
                        return app.slug ? (
                          <Link
                            to="/portal/apps/$appSlug"
                            params={{ appSlug: app.slug }}
                            className="text-sm hover:underline text-primary"
                          >
                            {app.name}
                          </Link>
                        ) : (
                          <span className="text-sm">{app.name}</span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {TABLE_TYPE_LABELS[table.table_type] ?? table.table_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {table.is_published ? (
                        <Badge variant="info">Published</Badge>
                      ) : (
                        <Badge variant="secondary">Draft</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(table.created_at_utc)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {table.updated_at_utc ? formatDate(table.updated_at_utc) : "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <GroupBadges permissions={tablePermissions.get(table.id)} />
                    </TableCell>
                    <TableCell
                      className={cn(
                        "sticky right-0 z-10 bg-background",
                        STICKY_RIGHT_SHADOW,
                      )}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Quick in-line actions, pinned right so they stay
                            reachable without scrolling: Add record + View table.
                            The full menu (3-dots) keeps every other action. */}
                        {table.is_published && tablePerms[table.id]?.can_submit && (
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="h-7 px-2"
                            title={`Add a record to ${table.name}`}
                          >
                            <Link
                              to="/portal/myapp/$tableId/submit"
                              params={{ tableId: String(table.id) }}
                              search={{}}
                            >
                              <ClipboardList className="h-3.5 w-3.5" />
                              <span className="ml-1 hidden sm:inline">Add</span>
                            </Link>
                          </Button>
                        )}
                        {table.is_published && tablePerms[table.id]?.can_view && (
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="h-7 px-2"
                            title={`View ${table.name}'s data`}
                          >
                            <Link
                              to="/portal/myapp/$tableId"
                              params={{ tableId: String(table.id) }}
                            >
                              <Table2 className="h-3.5 w-3.5" />
                              <span className="ml-1 hidden sm:inline">View</span>
                            </Link>
                          </Button>
                        )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Table actions">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {(() => {
                            const tp = tablePerms[table.id];
                            return table.is_published ? (
                              <>
                                {tp?.can_submit && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to="/portal/myapp/$tableId/submit"
                                      params={{ tableId: String(table.id) }}
                                      search={{}}
                                    >
                                      <ClipboardList className="h-4 w-4 mr-2" />
                                      Add Entry
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {tp?.can_view && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to="/portal/myapp/$tableId"
                                      params={{ tableId: String(table.id) }}
                                    >
                                      <Table2 className="h-4 w-4 mr-2" />
                                      View Data
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {tp?.can_edit && canStructure && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to="/portal/myapp/$tableId/edit"
                                      params={{ tableId: String(table.id) }}
                                    >
                                      <Settings className="h-4 w-4 mr-2" />
                                      View Structure
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {tp?.can_edit && canCreate && (
                                  <DropdownMenuItem onClick={() => handleCloneClick(table)}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Clone
                                  </DropdownMenuItem>
                                )}
                                {canSettings && (
                                  <DropdownMenuItem onClick={() => openAssignApp([table])}>
                                    <LayoutGrid className="h-4 w-4 mr-2" />
                                    Assign to App…
                                  </DropdownMenuItem>
                                )}
                                {tp?.is_owner && (
                                  <>
                                    <DropdownMenuItem asChild>
                                      <Link
                                        to="/portal/myapp/$tableId/permissions"
                                        params={{ tableId: String(table.id) }}
                                      >
                                        <ShieldCheck className="h-4 w-4 mr-2" />
                                        Permissions
                                      </Link>
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {tp?.can_edit && canHide && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleToggleLock(table)}>
                                      {table.is_locked ? (
                                        <>
                                          <Unlock className="h-4 w-4 mr-2" />
                                          Unlock
                                        </>
                                      ) : (
                                        <>
                                          <Lock className="h-4 w-4 mr-2" />
                                          Lock
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleToggleHidden(table)}>
                                      {table.is_hidden ? (
                                        <>
                                          <Eye className="h-4 w-4 mr-2" />
                                          Show
                                        </>
                                      ) : (
                                        <>
                                          <EyeOff className="h-4 w-4 mr-2" />
                                          Hide
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {tp?.can_delete_table && (
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteClick(table)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </>
                            ) : (
                              <>
                                {tp?.can_edit && canStructure && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to="/portal/myapp/$tableId/edit"
                                      params={{ tableId: String(table.id) }}
                                    >
                                      <Pencil className="h-4 w-4 mr-2" />
                                      Edit Table
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {tp?.can_edit && canCreate && (
                                  <DropdownMenuItem onClick={() => handleCloneClick(table)}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Clone
                                  </DropdownMenuItem>
                                )}
                                {canSettings && (
                                  <DropdownMenuItem onClick={() => openAssignApp([table])}>
                                    <LayoutGrid className="h-4 w-4 mr-2" />
                                    Assign to App…
                                  </DropdownMenuItem>
                                )}
                                {tp?.is_owner && (
                                  <>
                                    <DropdownMenuItem asChild>
                                      <Link
                                        to="/portal/myapp/$tableId/permissions"
                                        params={{ tableId: String(table.id) }}
                                      >
                                        <ShieldCheck className="h-4 w-4 mr-2" />
                                        Permissions
                                      </Link>
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {tp?.can_edit && canHide && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleToggleLock(table)}>
                                      {table.is_locked ? (
                                        <>
                                          <Unlock className="h-4 w-4 mr-2" />
                                          Unlock
                                        </>
                                      ) : (
                                        <>
                                          <Lock className="h-4 w-4 mr-2" />
                                          Lock
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleToggleHidden(table)}>
                                      {table.is_hidden ? (
                                        <>
                                          <Eye className="h-4 w-4 mr-2" />
                                          Show
                                        </>
                                      ) : (
                                        <>
                                          <EyeOff className="h-4 w-4 mr-2" />
                                          Hide
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {tp?.can_delete_table && (
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteClick(table)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </>
                            );
                          })()}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>

            {/* Pagination */}
            {pagination.totalCount > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={pageSizeOptions}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Table Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Create New Table</DialogTitle>
            <DialogDescription>
              Choose a table type and enter a name. A database table will be
              automatically generated when you publish.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 max-h-[60vh] overflow-y-auto px-1">
            <TableConfigPanel
              values={{
                name: newTable.name,
                description: newTable.description || "",
                table_type: (newTable.table_type as TableConfigValues["table_type"]) || "simple",
                version_mode: (newTable.version_mode as TableConfigValues["version_mode"]) || "filename",
                area_mode: (newTable.area_mode as TableConfigValues["area_mode"]) || "none",
                area_set_id: newTable.area_set_id ?? null,
                source_query: newTable.source_query ?? "",
                refresh_mode: (newTable.refresh_mode as TableConfigValues["refresh_mode"]) ?? "manual",
                refresh_interval_minutes: newTable.refresh_interval_minutes ?? null,
                unique_key_column: newTable.unique_key_column ?? null,
              }}
              onChange={(vals) => setNewTable({
                name: vals.name,
                description: vals.description || null,
                table_type: vals.table_type,
                version_mode: vals.version_mode,
                area_mode: vals.area_mode,
                area_set_id: vals.area_set_id,
                source_query: vals.source_query,
                refresh_mode: vals.refresh_mode,
                refresh_interval_minutes: vals.refresh_interval_minutes,
                unique_key_column: vals.unique_key_column,
              })}
              visibleTypes={
                isPlatformAdmin
                  ? ["simple", "advanced", "latest", "versioned", "virtual", "bridge"]
                  : ["simple", "advanced", "latest", "versioned", "virtual"]
              }
              namePosition="top"
              compact
              areaSets={areaSets}
              loadingAreaSets={loadingAreaSets}
              token={token}
              onValidationStateChange={({ nameError, nameValidating }) => {
                setCreateNameError(nameError);
                setCreateNameValidating(nameValidating);
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={creating || !newTable.name.trim() || !!createNameError || createNameValidating}
            >
              {creating ? "Creating..." : "Create Table"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Table</DialogTitle>
            <DialogDescription>
              {checkingDeps
                ? "Checking for dependencies..."
                : tableDeps && !tableDeps.can_delete
                  ? `"${tableToDelete?.name}" cannot be deleted because it is referenced by other tables.`
                  : `This will mark "${tableToDelete?.name}" as deleted. The data will be preserved for recovery.`}
            </DialogDescription>
          </DialogHeader>

          {checkingDeps && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!checkingDeps && tableDeps && !tableDeps.can_delete && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm">
              <p className="font-medium text-destructive mb-2">Referenced by:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {tableDeps.dependencies.map((dep, i) => (
                  <li key={i}>
                    <span className="font-medium">{dep.table_name}</span>
                    {" "}&rarr; {dep.field_name}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-muted-foreground">
                Remove these references first, then try again.
              </p>
            </div>
          )}

          {!checkingDeps && tableDeps?.can_delete && tableToDelete?.is_published && (
            <div className="rounded-md border border-orange-500/50 bg-orange-500/5 p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-orange-700 dark:text-orange-400">
                    This table is published
                    {tableDeps.record_count != null && tableDeps.record_count > 0
                      ? ` and contains ${tableDeps.record_count.toLocaleString()} record${tableDeps.record_count === 1 ? "" : "s"}`
                      : ""}
                    .
                  </p>
                </div>
              </div>
            </div>
          )}

          {!checkingDeps &&
            tableDeps?.can_delete &&
            tableDeps.report_impact &&
            (tableDeps.report_impact.custom_reports > 0 ||
              tableDeps.report_impact.dashboard_tiles > 0) && (
              <div className="rounded-md border border-orange-500/50 bg-orange-500/5 p-3 text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                  <p className="text-orange-700 dark:text-orange-400">
                    {[
                      tableDeps.report_impact.custom_reports > 0
                        ? `${tableDeps.report_impact.custom_reports} custom report${tableDeps.report_impact.custom_reports === 1 ? "" : "s"}`
                        : null,
                      tableDeps.report_impact.dashboard_tiles > 0
                        ? `${tableDeps.report_impact.dashboard_tiles} dashboard tile${tableDeps.report_impact.dashboard_tiles === 1 ? "" : "s"}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" and ")}{" "}
                    built on this table will stop working once it is deleted.
                  </p>
                </div>
              </div>
            )}

          {/* Type-to-confirm input */}
          {!checkingDeps && tableDeps?.can_delete && (
            <div className="space-y-2">
              <Label className="text-sm">
                Type <span className="font-bold">{tableToDelete?.name}</span> to confirm
              </Label>
              <Input
                value={confirmDeleteText}
                onChange={(e) => setConfirmDeleteText(e.target.value)}
                placeholder={tableToDelete?.name || ""}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && confirmDeleteText === tableToDelete?.name && !deleting) {
                    handleDeleteConfirm();
                  }
                }}
              />
            </div>
          )}

          {deleteError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
              {deleteError}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting || checkingDeps || (tableDeps != null && !tableDeps.can_delete) || confirmDeleteText !== tableToDelete?.name}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clone Table Dialog */}
      <Dialog open={cloneDialogOpen} onOpenChange={setCloneDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clone Table</DialogTitle>
            <DialogDescription>
              Create a copy of &quot;{tableToClone?.name}&quot; with all its
              fields. The new table will be a draft that you can modify before
              publishing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="clone-name">New Table Name</Label>
              <Input
                id="clone-name"
                placeholder="e.g., Customer (Copy)"
                value={cloneName}
                onChange={(e) => {
                  const val = e.target.value;
                  setCloneName(val);
                  setCloneNameError(null);
                  if (cloneNameTimerRef.current) clearTimeout(cloneNameTimerRef.current);
                  if (!val.trim()) { setCloneNameValidating(false); return; }
                  setCloneNameValidating(true);
                  cloneNameTimerRef.current = setTimeout(async () => {
                    try {
                      const result = await myappApi.tables.validateName(token, val.trim());
                      if (!result.is_valid) setCloneNameError(result.message);
                    } catch { /* backend enforces on save */ }
                    finally { setCloneNameValidating(false); }
                  }, 400);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && cloneName.trim() && !cloneNameError && !cloneNameValidating) {
                    handleCloneConfirm();
                  }
                }}
                className={cloneNameError ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {cloneNameValidating && (
                <p className="text-xs text-muted-foreground">Checking name...</p>
              )}
              {cloneNameError && (
                <p className="text-sm text-destructive">{cloneNameError}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCloneDialogOpen(false)}
              disabled={cloning}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloneConfirm}
              disabled={cloning || !cloneName.trim() || !!cloneNameError || cloneNameValidating}
            >
              <Copy className="h-4 w-4 mr-2" />
              {cloning ? "Cloning..." : "Clone Table"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Name cloned line tables Dialog */}
      <Dialog
        open={renameLinesOpen}
        onOpenChange={(open) => {
          if (!open) handleSkipLineNames();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name your new line tables</DialogTitle>
            <DialogDescription>
              &quot;{clonedHeaderName}&quot; was cloned with its line tables.
              They were auto-named to avoid clashes, give them intuitive names
              now, or skip and rename them later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {renameLines.map((line) => (
              <div key={line.id} className="space-y-2">
                <Label htmlFor={`line-name-${line.id}`}>{line.original}</Label>
                <Input
                  id={`line-name-${line.id}`}
                  value={line.name}
                  onChange={(e) => handleRenameLineChange(line.id, e.target.value)}
                  className={
                    line.error ? "border-red-500 focus-visible:ring-red-500" : ""
                  }
                />
                {line.validating && (
                  <p className="text-xs text-muted-foreground">Checking name...</p>
                )}
                {line.error && (
                  <p className="text-sm text-destructive">{line.error}</p>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleSkipLineNames}
              disabled={savingLineNames}
            >
              Skip for now
            </Button>
            <Button
              onClick={handleSaveLineNames}
              disabled={
                savingLineNames ||
                renameLines.some((l) => l.error || l.validating || !l.name.trim())
              }
            >
              {savingLineNames ? "Saving..." : "Save names"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Sheet */}
      <SettingsSheet
        selectedOrganisation={selectedOrganisation}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />

      {/* Create from Workbook Dialog */}
      <CreateFromWorkbookDialog
        open={workbookDialogOpen}
        onOpenChange={setWorkbookDialogOpen}
        onTableCreated={refresh}
      />

      {/* Bulk Group Access Dialog */}
      <BulkGroupAccessDialog
        serviceName="myapp"
        objectType="table"
        items={selectedItems}
        open={bulkGroupAccessOpen}
        onOpenChange={setBulkGroupAccessOpen}
        onComplete={() => { setSelectedIds(new Set()); fetchTablePermissions(); }}
      />

      {/* Assign to App dialog — bulk bar and per-row entry points share it */}
      {canSettings && (
        <AssignAppDialog
          items={assignAppItems}
          apps={apps}
          open={assignAppOpen}
          onOpenChange={setAssignAppOpen}
          onComplete={() => { setSelectedIds(new Set()); refresh(); fetchApps(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Extracted sub-components to keep the main function cleaner
// ---------------------------------------------------------------------------

function PageHeader({
  onSettingsClick,
  onCreateClick,
  onCreateFromWorkbookClick,
  canCreate,
  canImport,
  canSettings,
}: {
  onSettingsClick: () => void;
  onCreateClick: () => void;
  onCreateFromWorkbookClick: () => void;
  canCreate: boolean;
  canImport: boolean;
  canSettings: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div>
        <div className="flex items-center gap-4">
          <Link
            to="/portal/myapp"
            className="flex items-center gap-2 text-sm text-primary transition hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to myApp
          </Link>
        </div>
        <h1 className="mt-3 text-2xl md:text-4xl font-bold text-slate-900">Tables</h1>
        <p className="mt-1 md:mt-2 text-base md:text-lg text-slate-600">
          Create and manage your data tables
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        {canSettings && (
          <Button
            variant="outline"
            onClick={onSettingsClick}
            className="rounded-full border-slate-200 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <Settings2 className="mr-2 h-4 w-4" />
            Settings
          </Button>
        )}
        {(canCreate || canImport) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="rounded-full">
                <Plus className="h-4 w-4 mr-2" />
                New Table
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canImport && (
                <DropdownMenuItem onClick={onCreateFromWorkbookClick}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  From Workbook
                </DropdownMenuItem>
              )}
              {canCreate && (
                <DropdownMenuItem onClick={onCreateClick}>
                  <Table2 className="h-4 w-4 mr-2" />
                  Blank Table
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

function SettingsSheet({
  selectedOrganisation,
  open,
  onOpenChange,
}: {
  selectedOrganisation: ReturnType<typeof usePortalData>["selectedOrganisation"];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <AppPropertiesSheet
      appName="myapp"
      testConnectionEndpoint="/api/v1/gateway/myapp/api/v1/test-connection"
      title="MyApp Settings"
      description="Configure the database connection for your MyApp tables."
      schemaHelp="The schema for your MyApp tables (e.g., myapp-demo)"
      organisationId={selectedOrganisation?.organisationId ?? ""}
      organisationName={
        selectedOrganisation?.organisationName ??
        selectedOrganisation?.data?.CompanyName
      }
      open={open}
      onOpenChange={onOpenChange}
    />
  );
}

const MAX_VISIBLE_BADGES = 2;

function GroupBadges({ permissions }: { permissions?: { group_name: string; role: string }[] }) {
  if (!permissions || permissions.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const visible = permissions.slice(0, MAX_VISIBLE_BADGES);
  const remaining = permissions.length - MAX_VISIBLE_BADGES;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-wrap gap-1">
        {visible.map((p, i) => (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-xs cursor-default">
                {p.group_name}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>{p.role}</TooltipContent>
          </Tooltip>
        ))}
        {remaining > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="text-xs cursor-default">
                +{remaining}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {permissions.slice(MAX_VISIBLE_BADGES).map((p) => `${p.group_name} (${p.role})`).join(", ")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
